import React, { createContext, useContext, useState, ReactNode } from 'react';
import PlaidTermsConsent from './plaid-terms-consent';
import PlaidLinkLauncher from './plaid-link';
import PlaidUnlink from './plaid-unlink';
import PlaidError from './plaid-error';
import PlaidFix from './plaid-fix';
import PlaidRelink from './plaid-relink';
import { usePlaidModalVisibility } from './plaid-modal-visibility-context';
import SegmentImpression, {
  ImpressionType,
} from '@components/segment-impression';

interface StepperContextType {
  requiresPlaidTermsConsent: boolean;
  setRequiresPlaidTermsConsent: (requiresConsent: boolean) => void;
  isRelinking: boolean;
  setIsRelinking: (isRelinking: boolean) => void;
}

export enum PlaidStep {
  PLAID_TERMS_CONSENT = 'PLAID_TERMS_CONSENT',
  PLAID_LINK = 'PLAID_LINK',
  PLAID_ERROR = 'PLAID_ERROR',
  PLAID_MISSING_INSTITUTION_ERROR = 'PLAID_MISSING_INSTITUTION_ERROR',
  PLAID_RELINK = 'PLAID_RELINK',
  PLAID_UNLINK = 'PLAID_UNLINK',
  PLAID_FIX = 'PLAID_FIX',
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

const StepperProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [requiresPlaidTermsConsent, setRequiresPlaidTermsConsent] =
    useState<boolean>(true);
  const [isRelinking, setIsRelinking] = useState<boolean>(false);

  return (
    <StepperContext.Provider
      value={{
        requiresPlaidTermsConsent,
        setRequiresPlaidTermsConsent,
        isRelinking,
        setIsRelinking,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export const useStepper = () => {
  const context = useContext(StepperContext);

  if (!context) {
    throw new Error('useStepper must be used within a StepperProvider');
  }
  return context;
};

const PlaidModalStepper = ({
  step,
  handleCloseModal,
  plaidModifiedLinkedAccounts,
  plaidUnlinkedAccounts,
}) => {
  const { setShowPlaidVisibility } = usePlaidModalVisibility();

  const renderStep = () => {
    switch (step) {
      case PlaidStep.PLAID_TERMS_CONSENT:
        return <PlaidTermsConsent handleCloseModal={handleCloseModal} />;
      case PlaidStep.PLAID_LINK:
        return (
          <PlaidLinkLauncher
            plaidModifiedLinkedAccounts={plaidModifiedLinkedAccounts}
          />
        );
      case PlaidStep.PLAID_UNLINK:
        return (
          <PlaidUnlink
            plaidUnlinkedAccounts={plaidUnlinkedAccounts}
            handleCloseModal={handleCloseModal}
          />
        );
      case PlaidStep.PLAID_ERROR:
        return <PlaidError />;
      case PlaidStep.PLAID_FIX:
        return <PlaidFix handleCloseModal={handleCloseModal} />;
      case PlaidStep.PLAID_RELINK:
        return (
          <PlaidRelink
            plaidModifiedLinkedAccounts={plaidModifiedLinkedAccounts}
          />
        );
      case PlaidStep.PLAID_MISSING_INSTITUTION_ERROR:
        return (
          <PlaidError
            buttonText="Link a different bank"
            errorTitle="Small hiccup — please try linking this bank tomorrow"
            failureMessage="We need to refresh our connection with this bank, which can take a few hours. Sorry for the confusion — you should be able to link other banks in the meantime."
            handlePrimaryCtaClick={() =>
              setShowPlaidVisibility({
                show: true,
                plaidStep: PlaidStep.PLAID_LINK,
              })
            }
          />
        );
    }
  };

  return <>{renderStep()}</>;
};

const PlaidModalStepperWithProvider = ({
  step,
  handleCloseModal,
  plaidModifiedLinkedAccounts,
  plaidUnlinkedAccounts,
}: {
  step: PlaidStep;
  handleCloseModal: () => void;
  plaidModifiedLinkedAccounts: () => void;
  plaidUnlinkedAccounts: () => void;
}) => (
  <StepperProvider>
    <SegmentImpression
      eventName="plaid_linking_screen"
      eventType={ImpressionType.PAGE}
    >
      <PlaidModalStepper
        handleCloseModal={handleCloseModal}
        plaidModifiedLinkedAccounts={plaidModifiedLinkedAccounts}
        plaidUnlinkedAccounts={plaidUnlinkedAccounts}
        step={step}
      />
    </SegmentImpression>
  </StepperProvider>
);

export default PlaidModalStepperWithProvider;
